<template>
    <form @submit.prevent="handleSubmit" class="contact-form">
        <div v-if="carDetails" class="car-reference">
            <h4>{{ $t('contact_form.regarding_car') }}</h4>
            <p>{{ carDetails.make }} {{ carDetails.model }} ({{ carDetails.year }})</p>
            <input type="hidden" name="car_make" v-model="form.car_make">
            <input type="hidden" name="car_model" v-model="form.car_model">
            <input type="hidden" name="car_year" v-model="form.car_year">
        </div>

        <div class="form-group">
            <label for="name">{{ $t('contact_form.name') }}:</label>
            <input 
                id="name" 
                v-model="form.name" 
                type="text" 
                :placeholder="$t('contact_form.name_placeholder')" 
                required 
            />
        </div>
        <div class="form-group">
            <label for="email">{{ $t('contact_form.email') }}:</label>
            <input 
                id="email" 
                v-model="form.email" 
                type="email" 
                required 
            />
        </div>
        <div class="form-group">
            <label for="phone">{{ $t('contact_form.phone') }}:</label>
            <input 
                id="phone" 
                v-model="form.phone" 
                type="tel" 
                :placeholder="$t('contact_form.phone_placeholder')" 
            />
        </div>
        <div class="form-group">
            <label for="message">{{ $t('contact_form.message') }}:</label>
            <textarea 
                id="message" 
                v-model="form.message" 
                :placeholder="carDetails ? $t('contact_form.message_about_car_placeholder') : $t('contact_form.message_placeholder')" 
                required
            ></textarea>
        </div>
        <div class="form-actions">
            <button 
                type="button" 
                @click="handleCancel" 
                class="cancel-btn"
            >
                {{ $t('contact_form.cancel') }}
            </button>
            <button 
                type="submit" 
                class="submit-btn"
                :disabled="loading"
            >
                <span v-if="loading" class="spinner"></span>
                <span v-else>{{ $t('contact_form.submit') }}</span>
            </button>
        </div>
    </form>
</template>

<script>
import { defineComponent, ref, watch } from 'vue'

export default defineComponent({
    name: 'ContactForm',
    props: {
        loading: {
            type: Boolean,
            default: false
        },
        carDetails: {
            type: Object,
            default: null
        }
    },
    emits: ['submit', 'cancel'],
    setup(props, { emit }) {
        const form = ref({
            name: '',
            email: '',
            phone: '',
            message: '',
            car_make: '',
            car_model: '',
            car_year: ''
        })

        watch(() => props.carDetails, (newVal) => {
            if (newVal) {
                form.value.car_make = newVal.make
                form.value.car_model = newVal.model
                form.value.car_year = newVal.year
            }
        }, { immediate: true })

        const handleSubmit = () => {
            emit('submit', form.value)
        }

        const handleCancel = () => {
            emit('cancel')
            resetForm()
        }

        const resetForm = () => {
            form.value = {
                name: '',
                email: '',
                phone: '',
                message: '',
                car_make: props.carDetails?.make || '',
                car_model: props.carDetails?.model || '',
                car_year: props.carDetails?.year || ''
            }
        }

        return {
            form,
            handleSubmit,
            handleCancel
        }
    }
})
</script>

<style scoped>
    .contact-form {
        display: flex;
        flex-direction: column;
        gap: 20px;
        margin-top: 20px;
    }

    .car-reference {
        background-color: #f5f5f5;
        padding: 15px;
        border-radius: 8px;
        margin-bottom: 15px;
    }

    .car-reference h4 {
        margin: 0 0 5px 0;
        color: #333;
        font-size: 1rem;
    }

    .car-reference p {
        margin: 0;
        font-weight: 500;
        color: #0066cc;
    }

    .form-group {
        display: flex;
        flex-direction: column;
    }

    label {
        margin-bottom: 5px;
        font-weight: 500;
    }

    input,
    textarea {
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 1rem;
    }

    textarea {
        min-height: 100px;
        resize: vertical;
    }

    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    button {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 1rem;
        transition: background-color 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
    }

    button:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .cancel-btn {
        background-color: #f5f5f5;
    }

    .cancel-btn:hover {
        background-color: #e0e0e0;
    }

    .submit-btn {
        background-color: #0066cc;
        color: white;
    }

    .submit-btn:hover:not(:disabled) {
        background-color: #0052a3;
    }

    .spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-radius: 50%;
        border-top-color: white;
        animation: spin 1s ease-in-out infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
</style>
